import React from 'react';
import { V1SectionContainer } from './V1SectionContainer';
import { Clock, Bell } from 'lucide-react';

interface Bulletin {
  id: string;
  title: string;
  content: string;
  status: string;
  createdAt: string;
  sentAt?: string;
}

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  sectionOption?: any;
  sectionData?: {
    bulletins?: Bulletin[];
  };
}

interface V1BulletinBoardSectionProps {
  section: Section;
}

/**
 * V1 Bulletin Board Section - FROZEN
 *
 * Displays event bulletins/announcements in chronological order.
 * Shows bulletin title, content, and creation date.
 *
 * DO NOT add V2 features to this component.
 */
export const V1BulletinBoardSection: React.FC<V1BulletinBoardSectionProps> = ({ section }) => {
  const bulletins = section.sectionData?.bulletins || [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  if (bulletins.length === 0) {
    return (
      <V1SectionContainer section={section}>
        {section.customTitle && (
          <h2 className="text-2xl font-bold mb-4">{section.customTitle}</h2>
        )}
        <div className="text-center py-8">
          <Bell className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-500">No bulletins posted yet.</p>
        </div>
      </V1SectionContainer>
    );
  }

  return (
    <V1SectionContainer section={section}>
      {section.customTitle && (
        <h2 className="text-2xl font-bold mb-6">{section.customTitle}</h2>
      )}

      <div className="space-y-4">
        {bulletins.map((bulletin) => (
          <div
            key={bulletin.id}
            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {bulletin.title}
              </h3>
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 mr-1" />
                {formatDate(bulletin.createdAt)}
              </div>
            </div>

            <div
              className="text-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: bulletin.content }}
            />

            {bulletin.status && bulletin.status !== 'sent' && (
              <div className="mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {bulletin.status}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </V1SectionContainer>
  );
};